import Input from '../../../components/core/input'
import {SimpleBlueButton} from '../../../components/core/button'
import ButtonLoader from '../../../components/core/button/ButtonLoader'
import {Formik} from 'formik'
import {useEffect, useState} from 'react'
import {channelTypeSchema, telegramSchema, webhookSchema} from '../../../utils/validation'
import {addChannel, setAddedChannel} from '../../../stores/customer-settings'
import {useDispatch, useSelector} from 'react-redux'
import channelTypes from '../../../data/channelTypes.json'
import TextHelper, {trimStringValues} from '../../../utils/text'
import {toast} from 'react-toastify'
import LimitManager from '../../../utils/limitManager'
import CustomSelect from '../../../components/core/customSelect'
import {routes} from '../../../routes'
import {SimpleDiscordIcon, MSIcon, SlackIcon, WebhookIcon, TelegramIcon} from '../../../icons'
import localStorageKeys from '../../../utils/localStorageKeys'
import RenderedForPaid from '../../../components/pages/RenderedForPaid'
import plans from '../../../data/plans.json'
import limitUsageManager from '../../../utils/hooks/limitUsageManager'
import NavigationHelper from '../../../utils/navigation'
import {useNamespaceTranslations} from '../../../utils/hooks/useNamespacesTranslations'
import {envConfig} from '../../../utils/env'
import TelegramVerificationFlow from '../../../components/pages/notifications/Telegram'

const {baseUrl, slackClientId, discordClientId} = envConfig

const AddTeamChannel = ({closePopup, channels, type, teamId, setType, setModalData}) => {
    const text = useNamespaceTranslations('notifications')
    const usageLimits = useNamespaceTranslations('usage-limits')
    const [isProcessing, setIsProcessing] = useState(false)
    const [schema, setSchema] = useState(channelTypeSchema())
    const [active, setActive] = useState(null)
    const currentPlan = localStorage.getItem(localStorageKeys.PLAN)
    const isWebhookChannelDisabled = !LimitManager.getWebhookChannelIsEnabled(usageLimits)
    const isSlackChannelDisabled = !LimitManager.getSlackChannelIsEnabled(usageLimits)
    const isMSTeamChannelDisabled = !LimitManager.getMSTeamsChannelIsEnabled(usageLimits)
    const isDiscordChannelDisabled = !LimitManager.getDiscordChannelIsEnabled(usageLimits)
    const isTelegramChannelDisabled = !LimitManager.getTelegramChannelIsEnabled(usageLimits)
    const slackBaseUrl = 'https://slack.com'
    const discordBaseUrl = 'https://discord.com'
    const organisationLimitUsages = useSelector(state => state.auth.organisationLimitUsages)
    const addedChannel = useSelector(state => state.customer_settings.addedChannel)
    const getChannelLimit = (limitKey) => {
        const {value, usage} = limitUsageManager(
            usageLimits.limit_modules.incident_management.module,
            limitKey,
            organisationLimitUsages
        )
        return {value, usage}
    }

    const createTypeConfig = (limitKey) => ({
        limit: getChannelLimit(limitKey)?.value,
        usage: getChannelLimit(limitKey)?.usage,
        key: limitKey
    })

    const types = {
        [channelTypes.types.webhook]: createTypeConfig(
            usageLimits.limit_keys.per_user_webhook_channel_count.limit_key
        ),
        [channelTypes.types.slack]: createTypeConfig(
            usageLimits.limit_keys.slack_channel_count.limit_key
        ),
        [channelTypes.types.ms_teams]: createTypeConfig(
            usageLimits.limit_keys.ms_teams_channel_count.limit_key
        ),
        [channelTypes.types.discord]: createTypeConfig(
            usageLimits.limit_keys.discord_channel_count.limit_key
        ),
        [channelTypes.types.telegram]: createTypeConfig(
            usageLimits.limit_keys.telegram_channel_count.limit_key
        )
    }

    const dispatch = useDispatch()

    const handleInfo = (index, state) => {
        state ? setActive(index) : setActive(null)
    }

    const channelTypeElement = (value, label, isDisabled, icon, schema) => {
        return {value, label, isDisabled, icon, schema}
    }

    const channelTypeOptions = [
        channelTypeElement(channelTypes.types.webhook, 'Webhook', isWebhookChannelDisabled, <WebhookIcon className="filled-icon"/>, webhookSchema()),
        channelTypeElement(channelTypes.types.slack, 'Slack', isSlackChannelDisabled, <SlackIcon/>, channelTypeSchema()),
        channelTypeElement(channelTypes.types.ms_teams, 'Microsoft teams', isMSTeamChannelDisabled, <MSIcon className="ms-icon"/>, channelTypeSchema()),
        channelTypeElement(channelTypes.types.discord, 'Discord', isDiscordChannelDisabled, <SimpleDiscordIcon/>, channelTypeSchema()),
        channelTypeElement(channelTypes.types.telegram, 'Telegram', isTelegramChannelDisabled, <TelegramIcon/>, telegramSchema())
    ]

    const [initialFormValues, setInitialFormValues] = useState({
        type: '',
        value: ''
    })

    const fieldNames = {
        channel_type: 'type',
        channel_value: 'value'
    }

    const isSameType = (value, name) => value === name

    const getSlackAuthenticationUrl = () => {
        const redirectUri = baseUrl + NavigationHelper.getLocalizedPath(routes.team_channels.paths).replace('/', '')
        const scope = 'incoming-webhook,users:read.email,users:read'
        return `${slackBaseUrl}/oauth/v2/authorize?client_id=${slackClientId}&redirect_uri=${redirectUri}&scope=${scope}`
    }

    const getDiscordAuthenticationUrl = () => {
        const redirectUri = `${baseUrl + NavigationHelper.getLocalizedPath(routes.team_channels.paths).replace('/', '')}?channel_type=discord&response_type=code`
        const permissions = '0'
        const scope = 'identify guilds bot webhook.incoming'
        return `${discordBaseUrl}/oauth2/authorize?client_id=${discordClientId}&permissions=${permissions}&redirect_uri=${redirectUri}&scope=${scope}`
    }

    const addTeamCahnnel = async(values, {setFieldError, resetForm}) => {
        setIsProcessing(true)
        if (isSameType(values.type.value, channelTypes.types.slack)) {
            window.location.href = getSlackAuthenticationUrl()
            return
        }
        if (isSameType(values.type.value, channelTypes.types.discord)) {
            window.location.href = getDiscordAuthenticationUrl()
            return
        }
        if (channels.some(item => item.new_value === values.value)) {
            toast.error(type === channelTypes.types.telegram ? text.telegram_already_used : text.webhook_already_used)
            setIsProcessing(false)
            return
        }
        const success = await dispatch(addChannel(true, teamId, trimStringValues({
            ...values,
            type: values.type.value
        }), setFieldError, resetForm))
        setIsProcessing(false)
        if (success) {
            closePopup()
        }
    }

    const getPaidText = values => {
        const value = values.type.value
        let data = {}

        Object.keys(types).forEach(item => {
            if (isSameType(value, item)) {
                data = {
                    channelLimit: types[item].limit,
                    channelCount: types[item]?.limit - types[item]?.usage
                }
            }
        })

        return data.channelCount > 0 && text.for_paid_channel
            .replace(':channel_limit', data.channelLimit)
            .replace(':channel_type', values.type.label)
            .replace(':channel_count', data.channelCount)
    }

    const isExceeded = values => {
        const value = values.type.value
        let condition
        Object.keys(types).forEach(item => {
            if (isSameType(value, item)) {
                condition = types[item].limit === types[item].usage
            }
        })

        return condition
    }

    const checkSchema = (name, data, setFieldValue) => {
        setFieldValue(name, data)
        setSchema(channelTypeOptions.find(item => item.value === data.value).schema)
    }

    useEffect(() => {
        if (type) {
            setInitialFormValues(prev => ({
                ...prev,
                type: channelTypeOptions.find(item => item.value === type)
            }))

            setSchema(channelTypeOptions.find(item => item.value === type).schema)
        }
    }, [type])

    useEffect(() => {
        if (addedChannel?.priority) {
            setModalData(prev => ({
                ...prev,
                status: true,
                codeModal: addedChannel?.new_value_verified === false,
                value: addedChannel?.new_value,
                type: addedChannel?.type,
                channel: addedChannel
            }))
            dispatch(setAddedChannel({}))
        }
    }, [addedChannel])

    return (
        <Formik initialValues={initialFormValues}
            enableReinitialize={true}
            onSubmit={addTeamCahnnel}
            validationSchema={schema}
            validateOnBlur={false}
            validateOnChange={false}>
            {({values, errors, handleSubmit, setFieldValue, handleChange}) => (
                <form onSubmit={handleSubmit} className="modal-body">
                    {values.type.value &&
                        <RenderedForPaid forPaidText={getPaidText(values) || text.for_paid_general} isExceeded={isExceeded(values)} isTextWithLimits={!!getPaidText(values)}/>}
                    <CustomSelect
                        placeholder={text.channel_type}
                        onChange={(name, data) => {
                            checkSchema(name, data, setFieldValue)
                            setType(data.value)
                        }}
                        infoText={text.webhook_type_info}
                        name={fieldNames.channel_type}
                        value={values.type}
                        options={channelTypeOptions}
                        defaultFirst={true}
                        checkboxClassName="multiselect"
                        activeInfo={active === fieldNames.channel_type}
                        handleInfo={(state) => handleInfo(fieldNames.channel_type, state)}
                        error={errors.type}
                    />
                    {isSameType(values.type.value, channelTypes.types.webhook) && (
                        <Input
                            type="url"
                            value={values.value}
                            inputIsDisabled={isExceeded(values)}
                            placeholder={text.webhook}
                            name={fieldNames.channel_value}
                            onChange={e => {
                                handleChange(e)
                                setFieldValue('value', TextHelper.getNormalizedUrl(e.target.value))
                            }}
                            infoText={text.webhook_input_info}
                            activeInfo={active === fieldNames.channel_value}
                            handleInfo={(state) => handleInfo(fieldNames.channel_value, state)}
                            error={errors.value}
                        />
                    )}
                    {isSameType(values.type.value, channelTypes.types.ms_teams) && (
                        <Input
                            type="url"
                            value={values.value}
                            inputIsDisabled={isExceeded(values)}
                            placeholder={text.ms_teams_url}
                            name={fieldNames.channel_value}
                            onChange={handleChange}
                            infoText={text.ms_teams_input_info}
                            activeInfo={active === fieldNames.channel_value}
                            handleInfo={(state) => handleInfo(fieldNames.channel_value, state)}
                            error={errors.value}
                        />
                    )}
                    {isSameType(values.type.value, channelTypes.types.telegram) && (
                        <Input
                            type="text"
                            value={values.value}
                            inputIsDisabled={isExceeded(values)}
                            placeholder={text.telegram_chat_id}
                            name={fieldNames.channel_value}
                            onChange={handleChange}
                            infoText={text.telegram_channel_info}
                            activeInfo={active === fieldNames.channel_value}
                            handleInfo={(state) => handleInfo(fieldNames.channel_value, state)}
                            error={errors.value}
                        />
                    )}
                    {<SimpleBlueButton
                        type="submit"
                        disabled={isProcessing || isExceeded(values) || currentPlan === plans.free_v2.key}>
                        {isProcessing && <ButtonLoader/>}
                        {(values.type.value === channelTypes.types.slack || values.type.value === channelTypes.types.discord) ? text.add : text.submit}
                    </SimpleBlueButton>}
                    {isSameType(values.type.value, channelTypes.types.telegram) && <TelegramVerificationFlow text={text}/>}
                </form>
            )}
        </Formik>
    )
}

export default AddTeamChannel
